/**
 * Helper functions for generating responsive image URLs and srcsets
 * using images.weserv.nl and cdn.statically.io for on-the-fly image optimization
 */

// Optimized widths for most images (mobile to desktop) - reduced for better performance
export const DEFAULT_WIDTHS = [400, 600, 800, 1000];

// Widths for hero images (full viewport) - optimized for actual display sizes
export const HERO_WIDTHS = [768, 1024, 1366, 1600];

// Widths for content images - reduced for faster loading
export const CONTENT_WIDTHS = [400, 600, 800];

// Widths for card images - optimized for card contexts
export const CARD_WIDTHS = [300, 400, 600];

// Widths for small images like logos
export const SMALL_WIDTHS = [64, 96, 128, 160];

// Production domain for image optimization
const PRODUCTION_DOMAIN = 'housebeat.com';

/**
 * Check if we're in a preview/development environment
 */
function isPreviewEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.includes('lovable.app') || 
         hostname.includes('lovableproject.com') ||
         hostname.includes('localhost') ||
         hostname.includes('127.0.0.1');
}

/**
 * Check if we're in production environment
 */
function isProductionEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === PRODUCTION_DOMAIN || hostname === `www.${PRODUCTION_DOMAIN}`;
}

/**
 * Check if image is a local image (stored on same origin)
 */
function isLocalImage(src: string): boolean {
  return src.startsWith('/') && !src.startsWith('//');
}

/**
 * Build a responsive image URL using images.weserv.nl or cdn.statically.io
 */
export function buildResponsiveUrl(
  src: string, 
  options: { 
    width?: number; 
    quality?: number; 
    format?: 'auto' | 'webp' | 'jpeg' | 'png';
    provider?: 'weserv' | 'statically';
    context?: 'hero' | 'content' | 'card' | 'small';
    skipOptimization?: boolean;
  } = {}
): string {
  const { width, quality, format = 'webp', provider = 'weserv', context, skipOptimization } = options;
  
  // Context-aware quality optimization
  const defaultQuality = context === 'hero' ? 60 : context === 'content' ? 65 : 70;
  const finalQuality = quality || defaultQuality;
  
  // Skip proxy for data/blob/file URLs or localhost
  if (src.startsWith('data:') || src.startsWith('blob:') || src.startsWith('file:') || 
      src.includes('localhost') || src.includes('127.0.0.1')) {
    return src;
  }
  
  // In preview environments, return the original src for relative URLs
  // since the CDN can't access preview domains
  if (isPreviewEnvironment() && src.startsWith('/')) {
    return encodeURI(src);
  }
  
  // Skip CDN optimization when explicitly requested (e.g., for small pre-optimized images)
  if (skipOptimization) {
    return src;
  }
  
  try {
    // Convert relative URLs to absolute URLs using production domain
    // This ensures image optimization works in production
    const absoluteSrc = src.startsWith('/') 
      ? `https://${PRODUCTION_DOMAIN}${src}` 
      : src;
    
    const url = new URL(absoluteSrc);
    
    if (provider === 'statically') {
      // Use Statically service - fix URL construction
      const params = new URLSearchParams();
      
      if (width) {
        params.set('w', width.toString());
      }
      
      if (finalQuality !== 70) {
        params.set('q', finalQuality.toString());
      }
      
      if (format !== 'auto' && format !== 'webp') {
        params.set('f', format);
      }
      
      const queryString = params.toString();
      // Fix: Include full hostname in Statically URL
      const baseUrl = `https://cdn.statically.io/img/${url.hostname}${url.pathname}`;
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    } else {
      // Use weserv.nl service (default)
      const weservUrl = url.protocol === 'https:' 
        ? `ssl:${url.hostname}${url.pathname}${url.search}` 
        : `${url.hostname}${url.pathname}${url.search}`;
      
      const params = new URLSearchParams({
        url: weservUrl,
        q: finalQuality.toString(),
        output: format, // Force WebP for better compression
        we: '1', // Enable WebP when supported
        il: '1', // Enable interlaced/progressive loading
      });
      
      if (width) {
        params.set('w', width.toString());
        params.set('h', Math.round(width * 0.75).toString()); // Maintain aspect ratio limit
      }
      
      return `https://images.weserv.nl/?${params.toString()}`;
    }
  } catch (error) {
    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to build responsive URL:', error, { src, options });
    }
    // Fallback to original src if URL parsing fails
    return src;
  }
}

/**
 * Generate a srcset string for responsive images with fallback support
 */
export function buildSrcSet(
  src: string, 
  widths: number[] = DEFAULT_WIDTHS, 
  quality?: number, 
  fallbackProvider?: 'statically',
  context?: 'hero' | 'content' | 'card' | 'small',
  skipOptimization?: boolean
): string {
  // If skipping optimization, return empty srcset (browser will use src)
  if (skipOptimization) {
    return '';
  }
  
  const provider = fallbackProvider || 'weserv';
  const srcset = widths
    .map(width => `${buildResponsiveUrl(src, { width, quality, provider, context, format: 'webp' })} ${width}w`)
    .join(', ');
    
  return srcset;
}

/**
 * Generate appropriate sizes attribute based on image context
 */
export function getSizesForContext(context: 'hero' | 'content' | 'card' | 'small' | 'custom', customSizes?: string): string {
  if (customSizes) return customSizes;
  
  switch (context) {
    case 'hero':
      return '100vw';
    case 'content':
      return '(min-width: 1024px) 60vw, (min-width: 768px) 80vw, 100vw';
    case 'card':
      return '(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 100vw';
    case 'small':
      return '(min-width: 768px) 120px, 80px';
    default:
      return '100vw';
  }
}

/**
 * Get appropriate widths based on image context
 */
export function getWidthsForContext(context: 'hero' | 'content' | 'card' | 'small' | 'custom'): number[] {
  switch (context) {
    case 'hero':
      return HERO_WIDTHS;
    case 'content':
      return CONTENT_WIDTHS;
    case 'card':
      return CARD_WIDTHS;
    case 'small':
      return SMALL_WIDTHS;
    case 'custom':
    default:
      return DEFAULT_WIDTHS;
  }
}

/**
 * Helper function to handle image errors with retry logic
 */
export function handleImageError(
  event: Event, 
  originalSrc: string, 
  onRetry: (src: string, srcset?: string) => void,
  hasTriedStatically: boolean = false,
  hasTriedOriginal: boolean = false
) {
  if (process.env.NODE_ENV === 'development') {
    console.warn('Image load error, attempting fallback:', { originalSrc, hasTriedStatically, hasTriedOriginal });
  }
  
  if (!hasTriedStatically) {
    // Try Statically proxy
    const staticallyUrl = buildResponsiveUrl(originalSrc, { width: 800, provider: 'statically' });
    const staticallysrcset = buildSrcSet(originalSrc, DEFAULT_WIDTHS, 70, 'statically');
    onRetry(staticallyUrl, staticallysrcset);
  } else if (!hasTriedOriginal) {
    // Fall back to original image
    onRetry(originalSrc);
  }
  // If both failed, let the component handle the error state
}